import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Badge } from 'components/ui/badge';
import { getPortalPaymentStatus, type PortalPaymentStatus } from 'modules/portal-plans/api';

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

const som = (value: number) => `${value.toLocaleString('uz-UZ')} so‘m`;

interface PortalPaymentReturnProps {
  orderId: string;
  /** Receives the learner's balance once a Payme top-up is credited. */
  onBalance: (balanceSom: number) => void;
  onDismiss: () => void;
  /** The dismiss button's label; on the catalog it continues to the tests. */
  dismissLabel?: string;
}

export function PortalPaymentReturn({ orderId, onBalance, onDismiss, dismissLabel = 'Testlarga davom etish' }: PortalPaymentReturnProps) {
  const queryClient = useQueryClient();
  // Read through a ref so a new callback identity never restarts the polling.
  const onBalanceRef = useRef(onBalance);
  onBalanceRef.current = onBalance;
  const [status, setStatus] = useState<PortalPaymentStatus | null>(null);
  const [error, setError] = useState(false);
  const [waiting, setWaiting] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let checks = 0;
    setStatus(null); setError(false); setWaiting(true);
    const poll = async () => {
      try {
        const result = await getPortalPaymentStatus(orderId, controller.signal);
        if (controller.signal.aborted) return;
        setStatus(result); setError(false);
        if (result.activated) {
          setWaiting(false);
          setDialogOpen(true);
          if (result.kind === 'portal_balance_topup') {
            if (typeof result.balanceSom === 'number') onBalanceRef.current(result.balanceSom);
          } else {
            void queryClient.invalidateQueries({ queryKey: ['portal-subscription'] });
            void queryClient.refetchQueries({ queryKey: ['portal-subscription'] });
            void queryClient.invalidateQueries({ queryKey: ['public-tests'] });
            void queryClient.refetchQueries({ queryKey: ['public-tests'] });
            void queryClient.invalidateQueries({ queryKey: ['portal-tests'] });
          }
          return;
        }
        if (result.state === 'cancelled' || result.state === 'refunded') {
          setWaiting(false);
          return;
        }
      } catch {
        if (controller.signal.aborted) return;
        setError(true);
      }
      checks += 1;
      if (checks >= 10) { setWaiting(false); return; }
      timer = setTimeout(poll, 3000);
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [orderId, attempt, queryClient]);

  const activated = status?.activated;
  const topUp = status?.kind === 'portal_balance_topup';
  const startsLater = status?.periodStart && new Date(status.periodStart).getTime() > Date.now();
  const cancelled = status?.state === 'cancelled' || status?.state === 'refunded';
  const message = activated ? topUp
    ? `Balansingizga ${som(status.amount ?? 0)} tushdi. Endi testni boshlashingiz mumkin.`
    : startsLater
    ? `To‘lov tasdiqlandi. ${status.planName} obunangiz ${new Date(status.periodStart!).toLocaleDateString()} dan boshlanadi.`
    : `${status.planName} obunangiz faollashdi!`
    : cancelled ? topUp ? 'To‘lov bekor qilingan. Balans to‘ldirilmadi.' : 'To‘lov bekor qilingan. Obuna faollashtirilmadi.'
    : error ? 'To‘lov holatini tekshirib bo‘lmadi. Qayta tekshiring.'
    : waiting ? 'To‘lov tasdiqlanishi kutilmoqda…'
    : 'To‘lov hali tasdiqlanmadi. Birozdan keyin qayta tekshiring.';

  const handleDismiss = () => {
    setDialogOpen(false);
    onDismiss();
  };

  return (
    <>
      <section className="space-y-3 rounded-2xl border border-border bg-card p-4">
        <p role={error || cancelled ? 'alert' : 'status'} className="text-sm font-semibold">{message}</p>
        <div className="flex gap-2">
          {!activated && !cancelled && !waiting && <Button size="sm" onClick={() => setAttempt(value => value + 1)}>Qayta tekshirish</Button>}
          <Button size="sm" variant="outline" onClick={handleDismiss}>{dismissLabel}</Button>
        </div>
      </section>

      {activated && !topUp && (
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) onDismiss();
          }}
        >
          <DialogContent className="max-w-md p-6 sm:p-7">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
              <Badge className="mb-2 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">
                Muvaffaqiyatli to‘lov
              </Badge>
              <DialogTitle className="text-xl font-bold text-foreground">
                Tabriklaymiz! Obunangiz faollashdi 🎉
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-muted-foreground">
                <strong className="text-foreground">{status?.planName}</strong> obunangiz muvaffaqiyatli faollashtirildi. Endi barcha testlar va imkoniyatlar siz uchun ochiq!
              </DialogDescription>

              {status?.periodEnd && (
                <div className="mt-5 w-full rounded-xl border border-border bg-muted/30 p-3.5 text-left text-xs text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">Amal qilish muddati:</span>
                    <span className="font-semibold text-foreground">
                      {new Date(status.periodEnd).toLocaleDateString('uz-UZ')} gacha
                    </span>
                  </div>
                </div>
              )}

              <Button
                className="mt-6 w-full h-11 text-[15px] font-semibold"
                onClick={handleDismiss}
              >
                {dismissLabel === 'Testlarga davom etish' ? 'Testlarni boshlash' : dismissLabel}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {activated && topUp && (
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) onDismiss();
          }}
        >
          <DialogContent className="max-w-md p-6 sm:p-7">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
              <DialogTitle className="text-xl font-bold text-foreground">
                Balansingiz to‘ldirildi!
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-muted-foreground">
                Balansingizga <strong className="text-foreground">{som(status?.amount ?? 0)}</strong> muvaffaqiyatli tushdi. Endi testni boshlashingiz mumkin.
              </DialogDescription>
              <Button
                className="mt-6 w-full h-11 text-[15px] font-semibold"
                onClick={handleDismiss}
              >
                {dismissLabel}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
